import { useState, useEffect } from "react";
import { Check, Search, Filter, Info } from "lucide-react";
import Dropdown from "./Dropdown";
import FilterModal from "./FilterModal";
import { getUtilityPremise, assignServiceArea } from "../../api";
import { useToast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

// Keep these if they are truly static or move them to a constants file
const premiseOptions = [
  { value: "residential", label: "Residential Area" },
  { value: "commercial", label: "Commercial Area" },
  { value: "industrial", label: "Industrial Area" },
];

const cityOptions = [
  { value: "new-york", label: "New York" },
  { value: "los-angeles", label: "Los Angeles" },
  { value: "chicago", label: "Chicago" },
];

const ServiceAreaMapping = ({
  tableData,
  setTableData,
  selectedRows,
  setSelectedRows,
  searchTerm,
  setSearchTerm,
  activeTab,
  progress,
  getMappedCount,
  tableColumns,
  uploadId,
  setHasPremiseIssue,
}) => {
  const { toast } = useToast();

  const [selectedServiceArea, setSelectedServiceArea] = useState("");
  const [availableServiceAreaOptions, setAvailableServiceAreaOptions] =
    useState([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({ city: "", premise: "" });
  const [loadingServiceArea, setLoadingServiceArea] = useState(true);
  const [errorFetchingServiceArea, setErrorFetchingServiceArea] =
    useState(null);
  const remoteUtilityId = useRemoteUtilityId();

  useEffect(() => {
    const fetchServiceAreas = async () => {
      try {
        setLoadingServiceArea(true);
        setErrorFetchingServiceArea(null);

        const data = await getUtilityPremise(remoteUtilityId);

        if (data && data.result) {
          const formattedOptions = data.result.map((item) => ({
            value: item.name,
            label: item.name,
          }));
          setAvailableServiceAreaOptions(formattedOptions);
        } else {
          setAvailableServiceAreaOptions([]);
        }
      } catch (error) {
        setErrorFetchingServiceArea(
          "Failed to load service areas. Please try again."
        );
      } finally {
        setLoadingServiceArea(false);
      }
    };

    fetchServiceAreas();
  }, []);

  // Handle assignment
  // const handleAssignToSelected = async () => {
  //   if (selectedRows.length === 0 || !selectedServiceArea) {
  //     console.log(
  //       "Assignment blocked: selectedRows:",
  //       selectedRows.length,
  //       "selectedServiceArea:",
  //       selectedServiceArea
  //     );
  //     return;
  //   }

  //   // Use the correct field name from your data structure
  //   const selectedConsumerNos = tableData
  //     .filter((row) => selectedRows.includes(row.id))
  //     .map((row) => row.consumerNo); // This matches your data structure

  //   console.log("Selected consumer numbers:", selectedConsumerNos);
  //   console.log("Selected service area:", selectedServiceArea);

  //   const payload = {
  //     consumer_no: selectedConsumerNos,
  //     service_premise: selectedServiceArea,
  //     data_upload_id: uploadId,
  //   };

  //   console.log("Assigning service area with payload:", payload);

  //   try {
  //     const response = await assignServiceArea(payload);
  //     console.log("Service area assigned successfully:", response);

  //     // Find the selected service area label
  //     const serviceAreaLabel =
  //       availableServiceAreaOptions.find(
  //         (opt) => opt.value === selectedServiceArea
  //       )?.label || selectedServiceArea;

  //     console.log("Service area label to assign:", serviceAreaLabel);

  //     // Update table data with the assigned service area
  //     setTableData((prevTableData) => {
  //       const updatedData = prevTableData.map((row) => {
  //         if (selectedRows.includes(row.id)) {
  //           console.log(
  //             "Updating row:",
  //             row.id,
  //             "with service area:",
  //             serviceAreaLabel
  //           );
  //           return {
  //             ...row,
  //             serviceArea: serviceAreaLabel,
  //             serviceAreaId: selectedServiceArea,
  //             servicePremise: selectedServiceArea, // Update the original field too
  //           };
  //         }
  //         return row;
  //       });
  //       console.log("Updated table data:", updatedData);
  //       return updatedData;
  //     });

  //     // Reset selections
  //     setSelectedRows([]);
  //     setSelectedServiceArea("");

  //     console.log("Assignment completed successfully");
  //   } catch (error) {
  //     console.error("Error assigning service area:", error);
  //     alert("Failed to assign service area. Please try again.");
  //   }
  // };

  const handleAssignToSelected = async () => {
    if (selectedRows.length === 0 || !selectedServiceArea) {
      return;
    }

    const selectedConsumerNos = tableData
      .filter((row) => selectedRows.includes(row.id))
      .map((row) => row.consumerNo);

    const payload = {
      consumer_no: selectedConsumerNos,
      service_premise: selectedServiceArea,
      data_upload_id: uploadId,
    };

    try {
      const response = await assignServiceArea(payload);

      const serviceAreaLabel =
        availableServiceAreaOptions.find(
          (opt) => opt.value === selectedServiceArea
        )?.label || selectedServiceArea;

      setTableData((prevTableData) =>
        prevTableData.map((row) =>
          selectedRows.includes(row.id)
            ? {
                ...row,
                serviceArea: serviceAreaLabel,
                serviceAreaId: selectedServiceArea,
                servicePremise: selectedServiceArea,
              }
            : row
        )
      );

      setSelectedRows([]);
      setSelectedServiceArea("");

      // ✅ Update parent issue state
      setHasPremiseIssue(false);
      toast({
        title: "Success",
        description: "Service area assignment successfully.",
        variant: "default",
      });
    } catch (error) {}
  };

  const handleRowSelect = (rowId) => {
    setSelectedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === tableData.length && tableData.length > 0) {
      setSelectedRows([]);
    } else {
      setSelectedRows(tableData.map((row) => row.id));
    }
  };

  // Filter data based on search term
  const filteredData = tableData.filter((row) =>
    Object.values(row).some(
      (value) =>
        value && String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleFilterApply = (newFilters) => {
    setFilters(newFilters);
  };

  return (
    <>
      {/* Progress Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900">Service Area Mapping</h3>
            <p className="text-sm text-blue-700 mt-1">
              Assign service areas to consumers based on your criteria
            </p>
            <p className="text-sm text-blue-600 mt-1">
              Progress: {progress}% ({getMappedCount("service")}/
              {tableData.length} records mapped)
            </p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        {/* Search and Assignment Controls */}
        <div className="flex items-center justify-between mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={16} className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search records..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
            />
          </div>

          <div className="flex items-center space-x-4">
            {/* Selection Info */}
            {selectedRows.length > 0 && (
              <div className="text-sm text-gray-600">
                {selectedRows.length} record
                {selectedRows.length !== 1 ? "s" : ""} selected
              </div>
            )}

            {loadingServiceArea ? (
              <div className="text-gray-500">Loading service areas...</div>
            ) : errorFetchingServiceArea ? (
              <div className="text-red-500">{errorFetchingServiceArea}</div>
            ) : (
              <Dropdown
                options={availableServiceAreaOptions}
                value={selectedServiceArea}
                onChange={setSelectedServiceArea}
                placeholder="Select service area"
                className="w-48"
              />
            )}

            <button
              onClick={handleAssignToSelected}
              disabled={selectedRows.length === 0 || !selectedServiceArea}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                selectedRows.length > 0 && selectedServiceArea
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              <Check size={16} />
              <span>Assign to Selected ({selectedRows.length})</span>
            </button>

            <button
              onClick={() => setIsFilterModalOpen(true)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Filter size={16} />
              <span>Filter</span>
            </button>
          </div>
        </div>

        {/* Table - Dynamically rendered based on API data */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-8 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === tableData.length &&
                      tableData.length > 0
                    }
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                {/* Dynamically render headers from tableColumns */}
                {tableColumns.map((column) => (
                  <th
                    key={column.accessor}
                    className="text-left py-3 px-4 font-medium text-gray-700 text-sm"
                  >
                    {column.Header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredData.length > 0 ? (
                filteredData.map((row) => (
                  <tr
                    key={row.id}
                    className={`hover:bg-gray-50 ${
                      selectedRows.includes(row.id) ? "bg-blue-50" : ""
                    }`}
                  >
                    <td className="px-4 py-3">
                      {/* <input
                        type="checkbox"
                        checked={selectedRows.includes(row.id)}
                        onChange={() => handleRowSelect(row.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      /> */}
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(row.id)} // reflect "Select All" state
                        disabled // prevent manual change
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 opacity-50 cursor-not-allowed"
                      />
                    </td>
                    {/* Dynamically render cells based on tableColumns */}
                    {tableColumns.map((column) => (
                      <td
                        key={`${row.id}-${column.accessor}`}
                        className="px-4 py-3 text-sm"
                      >
                        {/* Special handling for serviceArea column */}
                        {column.accessor === "serviceArea" ? (
                          row.serviceArea ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {row.serviceArea}
                            </span>
                          ) : (
                            <span className="text-gray-400">Not assigned</span>
                          )
                        ) : (
                          // Generic rendering for all other dynamic columns from API
                          <span className="text-gray-900">
                            {row[column.accessor] !== null &&
                            row[column.accessor] !== undefined
                              ? String(row[column.accessor])
                              : "-"}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={tableColumns.length + 1}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    {searchTerm
                      ? "No records found matching your search."
                      : "No records available."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filter Modal */}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={handleFilterApply}
        premiseOptions={premiseOptions}
        cityOptions={cityOptions}
      />
    </>
  );
};

export default ServiceAreaMapping;
